<template>
    <div>
        <p class="has-text-white mb-6">
            Turn on each certification you are working toward and we'll show its tools across your account. Anything you turn off stays on file; you can turn it back on any time.
        </p>

        <ui-card v-for="cert in certifications" :key="cert.certification_id" class="mb-4" :class="{'is-off': !cert.selected}">
            <div class="columns">
                <div class="column is-narrow">
                    <i class="fad fa-certificate fa-2x" :class="cert.selected ? 'has-text-primary' : 'has-text-grey-light'"></i>
                </div>
                <div class="column">
                    <div class="columns is-mobile is-vcentered mb-1">
                        <div class="column">
                            <div class="has-text-weight-bold is-size-5">{{ cert.certification.title }}</div>
                        </div>
                        <div class="column is-narrow">
                            <b-switch :value="cert.selected" type="is-success" :disabled="saving === cert.certification_id" @input="toggle(cert, $event)">
                                <span class="is-size-7 has-text-weight-bold">{{ cert.selected ? 'On' : 'Off' }}</span>
                            </b-switch>
                        </div>
                    </div>

                    <p class="has-text-grey" v-if="cert.certification.description">{{ cert.certification.description }}</p>

                    <div v-if="!cert.selected" class="is-size-7 has-text-grey mt-2">
                        <i class="far fa-circle-info mr-2"></i>Turned off. Courses, conferences and applications for this certification stay on your account; turn it on to use its tools.
                    </div>

                    <template v-if="cert.selected && cert.integration">
                        <h4 class="mt-5"><i class="fad fa-diagram-project mr-2"></i>Integrations</h4>
                        <div class="mb-3">
                            <strong>{{ cert.integration.title }}</strong>
                            <template v-if="cert.integration.url"> (<a :href="cert.integration.url" target="_blank">{{ cert.integration.url }}</a>)</template>
                        </div>
                        <div class="columns">
                            <div class="column is-4">
                                <img v-if="logo(cert)" :src="logo(cert)" :alt="cert.integration.title">
                            </div>
                            <div class="column"></div>
                            <div class="column is-5">
                                <b-field :label="numberLabel(cert)" label-position="inside">
                                    <b-input v-model="numbers[cert.certification_id]" :placeholder="numberPlaceholder(cert)" :disabled="!!cert.locked_at"></b-input>
                                </b-field>
                                <div class="is-size-7" v-if="cert.certification_id === 2">
                                    <a href="https://wiaa.finalforms.com/lookup_membership_id" target="_blank"><i class="fas fa-search mr-2"></i>Lookup WIAA Membership ID</a>
                                </div>
                                <div class="is-size-7 has-text-grey" v-if="cert.locked_at">
                                    <i class="fas fa-lock mr-2"></i>This ID was confirmed by {{ cert.integration.title }}. <a href="mailto:support@my247ed.com">Contact support</a> if it needs to be changed.
                                </div>
                                <div class="is-size-7 has-text-grey" v-else-if="cert.synced_at">
                                    <i class="fas fa-rotate mr-2"></i>Last synced {{ cert.synced_at | dateOnly }}.
                                </div>
                            </div>
                        </div>

                        <b-message type="is-warning" v-if="cert.conflicts">
                            <i class="fas fa-exclamation-triangle mr-2"></i>The {{ numberLabel(cert) }} you have entered is in use by another account. If you have multiple accounts using the same ID this may lead to complications in accurately tracking and synchronizing your records and certifications. Please verify your ID is correct or <a href="mailto:support@my247ed.com">contact support</a> for assistance.
                        </b-message>
                    </template>
                </div>
            </div>

            <template #footer v-if="cert.selected && cert.integration">
                <div class="columns">
                    <div class="column"></div>
                    <div class="column has-text-right">
                        <b-button type="is-primary" size="is-small" :disabled="!!cert.locked_at || saving === cert.certification_id" :loading="saving === cert.certification_id" @click="saveNumber(cert)"><i class="fas fa-check mr-2"></i>Save Changes</b-button>
                    </div>
                </div>
            </template>
        </ui-card>

        <div v-if="certificationsLoaded && !certifications.length" class="has-text-grey">No certifications are available yet.</div>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";
    import UiCard from "../UiCard";

    // Your Account > Certifications & Organizations: the switchboard. One card
    // per certification the platform knows, with the on/off switch and (while
    // on) the ID number for its integration. Turning a certification off keeps
    // the row and its number; only the switch changes.
    export default {
        name: "CertificationsSection",
        components: {UiCard},
        data() {
            return {
                numbers: {},
                saving: null,
            };
        },
        computed: {
            ...mapGetters(['certifications', 'certificationsLoaded']),
        },
        methods: {
            ...mapActions(['getCertifications', 'saveCertification']),
            syncNumbers() {
                const numbers = {};
                for (const c of this.certifications) numbers[c.certification_id] = c.certification_number || '';
                this.numbers = numbers;
            },
            numberLabel(cert) {
                if (cert.certification_id === 1) return 'OSPI Cert Number';
                if (cert.certification_id === 2) return 'WIAA Membership ID';
                return (cert.integration ? cert.integration.title : cert.certification.title) + ' ID';
            },
            numberPlaceholder(cert) {
                if (cert.certification_id === 1) return 'ie. 000000F';
                if (cert.certification_id === 2) return 'ie. WA-0000-0000';
                return '';
            },
            logo(cert) {
                if (!cert.integration || !cert.integration.logo) return null;
                try {
                    return require('@/assets/partner_assets/' + cert.integration.logo);
                } catch (e) {
                    return null;
                }
            },
            toggle(cert, on) {
                this.saving = cert.certification_id;
                this.saveCertification({
                    certification_id: cert.certification_id,
                    integration_id: cert.integration_id,
                    certification_number: cert.certification_number,
                    selected: on,
                }).then(() => {
                    this.$notify('success', on ? 'Certification On' : 'Certification Off', on
                        ? `${cert.certification.title} tools are now available across your account.`
                        : `${cert.certification.title} tools are hidden. Your records stay on file.`, {duration: 5000, permanent: false});
                }).catch(() => {
                    this.$notify('error', 'Save Failed', 'Your change could not be saved. Please try again later.', {duration: 5000, permanent: false});
                }).finally(() => {
                    this.saving = null;
                });
            },
            saveNumber(cert) {
                this.saving = cert.certification_id;
                this.saveCertification({
                    certification_id: cert.certification_id,
                    integration_id: cert.integration_id,
                    certification_number: this.numbers[cert.certification_id],
                }).then(() => {
                    this.$notify('success', 'Settings Saved', 'Your changes have been successfully applied.', {duration: 5000, permanent: false});
                }).catch(err => {
                    if (err.response && err.response.status === 409) {
                        this.$notify('error', 'ID Locked', 'This ID is locked to your account. Please contact support to change it.', {duration: 8000, permanent: false});
                    } else {
                        this.$notify('error', 'Save Failed', 'Your changes could not be saved. Please try again later.', {duration: 5000, permanent: false});
                    }
                }).finally(() => {
                    this.saving = null;
                });
            },
        },
        watch: {
            certifications: {
                handler() {
                    this.syncNumbers();
                },
                immediate: true,
            },
        },
        mounted() {
            this.getCertifications().catch(err => console.error(err));
        },
    };
</script>

<style lang="scss" scoped>
    .is-off {
        opacity: .85;
    }
</style>
